.wrapper {
  position: relative;
}

.root {
  align-items: center;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  gap: 6px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;

  &:hover {
    border-color: var(--color-foreground);
  }
}

.root-size-sm {
  height: 32px;
  padding: 0 10px;
}

.root-size-default {
  height: 36px;
  padding: 0 12px;
}

.root-size-lg {
  height: 40px;
  padding: 0 14px;
}

.root-is-open {
  border-color: var(--color-focus);
  box-shadow: 0 0 0 2px var(--color-focus);
}

.root-is-disabled {
  background: var(--color-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

// Compact ghost-button trigger (legacy "Add Driver"/"Add Guide" affordance):
// no border/background, hugs its content, no chevron.
.root-appearance-button {
  background: none;
  border: none;
  border-radius: 6px;
  // Inherit the surrounding font-size so the consumer can size the trigger to
  // match adjacent controls.
  font-size: inherit;
  gap: 4px;
  height: auto;
  padding: 4px 6px;
  width: auto;

  &:hover {
    background: var(--color-surface);
    border-color: transparent;
  }

  .value,
  .placeholder {
    // Muted, matching adjacent ghost "Add … notes" text (Text variant="muted").
    color: var(--color-muted);
    flex: 0 1 auto;
  }
}

.icon {
  color: var(--color-muted);
  flex-shrink: 0;
}

.value {
  color: var(--color-foreground);
  flex: 1;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.placeholder {
  color: var(--color-placeholder);
  flex: 1;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  color: var(--color-muted);
  flex-shrink: 0;
  height: 16px;
  transition: transform 0.15s;
  width: 16px;
}

.chevronOpen {
  transform: rotate(180deg);
}

.clearBtn {
  align-items: center;
  background: none;
  border: 0;
  border-radius: 4px;
  color: var(--color-muted);
  cursor: pointer;
  display: inline-flex;
  height: 20px;
  justify-content: center;
  padding: 0;
  width: 20px;

  &:hover {
    background: var(--color-surface);
    color: var(--color-foreground);
  }
}

.dropdown {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  max-height: 320px;
  overflow: hidden;
  padding: 6px;
  position: absolute;
  top: 100%;
  width: 100%;
  z-index: 30;
}

// Portaled variant: fixed positioning (coords supplied inline) so the panel
// escapes `overflow: hidden` ancestors. `top`/`width` come from the trigger
// rect; clear the absolute-flow offsets.
.dropdown-portal {
  margin-top: 0;
  // Hard cap so a menu widened by `dropdownMinWidth` can never exceed the
  // viewport, whichever edge it is anchored to. Mirrors Select.
  max-width: calc(100vw - 16px);
  position: fixed;
  top: auto;
  width: auto;
  // Above the Modal overlay (z-index: 2000) so a portaled dropdown opened
  // inside a modal renders over it, not behind it. Mirrors the Select
  // component's portaled z-index (2100).
  z-index: 2100;
}

.searchRow {
  align-items: center;
  background: var(--color-surface);
  border-radius: 8px;
  display: flex;
  gap: 6px;
  padding: 6px 10px;
}

.searchIcon {
  color: var(--color-muted);
  flex-shrink: 0;
  height: 14px;
  width: 14px;
}

.searchInput {
  background: transparent;
  border: 0;
  color: var(--color-foreground);
  flex: 1;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--color-placeholder);
  }
}

.spinner {
  animation: spin 1s linear infinite;
  color: var(--color-muted);
  flex-shrink: 0;
  height: 14px;
  width: 14px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.optionList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style: none;
  margin: 6px 0 0 0;
  overflow-y: auto;
  padding: 0;
}

// Row wrapper: the selectable option button + any trailing action sit side by
// side (the action is a sibling, not nested, so it can be a real control).
.option-row {
  align-items: center;
  border-radius: 6px;
  display: flex;
  width: 100%;

  &:hover {
    background: var(--color-surface);
  }
}

.option {
  align-items: center;
  background: none;
  border: 0;
  border-radius: 6px;
  color: var(--color-foreground);
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: inherit;
  font-size: 14px;
  gap: 6px;
  min-width: 0;
  padding: 6px 10px;
  text-align: left;
  transition: background-color 0.1s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.optionSelected {
  background: var(--color-surface);
}

.checkSlot {
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  width: 16px;
}

.checkIcon {
  color: var(--color-foreground);
  flex-shrink: 0;
  height: 16px;
  width: 16px;
}

.optionLabel {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Rich-row variant (getOptionContent): flexible track that lets the consumer
// lay out its own icon / label / meta; min-width:0 keeps long names ellipsable.
.optionContent {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.optionTrailing {
  align-items: center;
  color: var(--color-muted);
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  padding-right: 10px;
}

.empty {
  color: var(--color-muted);
  font-size: 13px;
  padding: 12px;
  text-align: center;
}
